import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { confirmDialog } from '@/lib/confirmDialog'
import { useAuth } from '@/hooks/useAuth'
import { useTeam } from '@/hooks/useTeam'
import { canCreatePosting, getPostingLimitMessage, getPostingLimit } from '@/lib/postingLimits'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Loading from '@/components/common/Loading'
import ScrimmagePostingForm from '@/components/scrimmage/ScrimmagePostingForm'
import CalendarEventSelector from '@/components/scrimmage/CalendarEventSelector'
import { HOCKEY_AGE_GROUPS } from '@/lib/hockeyData'
import { toast } from 'sonner'

export default function ScrimmageManager() {
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  const [postings, setPostings] = useState([])
  const [scrimmageManager, setScrimmageManager] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [showCalendarSelector, setShowCalendarSelector] = useState(false)
  const [editingPosting, setEditingPosting] = useState(null)
  const [selectedCalendarEvent, setSelectedCalendarEvent] = useState(null)
  const [activeCount, setActiveCount] = useState(0)

  const isPro = team?.season_tier === 'paid'
  const canPost = canCreatePosting(activeCount, team?.season_tier)
  const limitMessage = getPostingLimitMessage(activeCount, team?.season_tier)

  useEffect(() => {
    if (!authLoading && !teamLoading) {
      if (!user) {
        navigate('/auth')
      } else if (!team) {
        navigate('/team-setup')
      } else {
        fetchData()
      }
    }
  }, [user, team, authLoading, teamLoading, navigate])

  const fetchData = async () => {
    if (!team?.id) return
    
    setLoading(true)
    try {
      // Fetch scrimmage manager
      const { data: manager } = await supabase
        .from('scrimmage_managers')
        .select('*')
        .eq('team_id', team.id)
        .maybeSingle()

      setScrimmageManager(manager)

      // Fetch postings
      const { data: postingsData, error } = await supabase
        .from('scrimmage_postings')
        .select('*')
        .eq('team_id', team.id)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true })

      if (error) throw error

      setPostings(postingsData || [])
      const active = postingsData?.filter(p => p.status === 'active').length || 0
      setActiveCount(active)
    } catch (err) {
      console.error('Error fetching scrimmage data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleNewPosting = () => {
    if (!canPost) {
      const limit = getPostingLimit(team?.season_tier)
      toast.error(`You have reached the limit of ${limit} active postings. Upgrade to Pro for unlimited postings!`)
      return
    }
    setEditingPosting(null)
    setSelectedCalendarEvent(null)
    
    // Show calendar selector if team has calendar synced
    if (team?.calendar_feed_url) {
      setShowCalendarSelector(true)
    } else {
      setShowForm(true)
    }
  }

  const handleEventSelected = (event) => {
    setSelectedCalendarEvent(event)
    setShowCalendarSelector(false)
    setShowForm(true)
  }

  const handleSkipCalendar = () => {
    setSelectedCalendarEvent(null)
    setShowCalendarSelector(false)
    setShowForm(true)
  }

  const handleEditPosting = (posting) => {
    setEditingPosting(posting)
    setShowForm(true)
  }

  const handleFormSuccess = () => {
    setShowForm(false)
    setShowCalendarSelector(false)
    setEditingPosting(null)
    setSelectedCalendarEvent(null)
    fetchData()
  }

  const handleFormCancel = () => {
    setShowForm(false)
    setShowCalendarSelector(false)
    setEditingPosting(null)
    setSelectedCalendarEvent(null)
  }

  const handleMarkFilled = async (posting) => {
    const confirmed = await confirmDialog({
      title: 'Mark as Filled',
      message: 'Mark this scrimmage as filled? It will be removed from the public bulletin board.',
      confirmText: 'Mark Filled',
      cancelText: 'Cancel'
    })

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('scrimmage_postings')
        .update({
          status: 'filled',
          filled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', posting.id)

      if (error) throw error

      fetchData()
    } catch (err) {
      console.error('Error marking posting as filled:', err)
      toast.error('Failed to update posting. Please try again.')
    }
  }

  const handleCancelPosting = async (posting) => {
    const confirmed = await confirmDialog({
      title: 'Cancel Posting',
      message: 'Cancel this scrimmage posting? It will be removed from the public bulletin board.',
      confirmText: 'Cancel Posting',
      cancelText: 'Keep Posting'
    })

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('scrimmage_postings')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', posting.id)

      if (error) throw error

      fetchData()
    } catch (err) {
      console.error('Error cancelling posting:', err)
      toast.error('Failed to cancel posting. Please try again.')
    }
  }

  const handleDeletePosting = async (posting) => {
    const confirmed = await confirmDialog({
      title: 'Delete Posting',
      message: 'Permanently delete this scrimmage posting? This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel'
    })

    if (!confirmed) return

    try {
      const { error } = await supabase
        .from('scrimmage_postings')
        .delete()
        .eq('id', posting.id)

      if (error) throw error

      fetchData()
    } catch (err) {
      console.error('Error deleting posting:', err)
      toast.error('Failed to delete posting. Please try again.')
    }
  }

  const handleReactivate = async (posting) => {
    try {
      const { error } = await supabase
        .from('scrimmage_postings')
        .update({
          status: 'active',
          filled_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', posting.id)

      if (error) throw error

      fetchData()
    } catch (err) {
      console.error('Error reactivating posting:', err)
      toast.error('Failed to reactivate posting. Please try again.')
    }
  }

  if (authLoading || teamLoading) {
    return <Loading text="Loading..." />
  }

  if (!team) {
    return null
  }

  // Check if scrimmage manager is set up
  const needsSetup = !scrimmageManager || !team.city || !team.state || !team.team_level

  if (needsSetup) {
    return (
      <div className="min-h-screen bg-brand-cream py-8">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="secondary"
                className="text-sm"
              >
                ← Back to Dashboard
              </Button>
            </div>
            
            <h1 className="font-heading text-3xl font-bold mb-6">
              Scrimmage Manager
            </h1>

            <Card>
              <h2 className="font-heading text-xl font-bold mb-4">
                Setup Required
              </h2>
              <p className="text-brand-slate mb-6">
                Before you can post scrimmages, please complete your scrimmage manager settings. This includes your team location, hockey level, and coordinator contact information.
              </p>
              <Button
                onClick={() => navigate('/settings?tab=scrimmage')}
                variant="primary"
              >
                Complete Setup in Settings
              </Button>
            </Card>
          </div>
        </Container>
      </div>
    )
  }

  if (showCalendarSelector) {
    return (
      <div className="min-h-screen bg-brand-cream py-8">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button
                onClick={() => setShowCalendarSelector(false)}
                variant="secondary"
                className="text-sm"
              >
                ← Back to Postings
              </Button>
            </div>

            <CalendarEventSelector
              team={team}
              onEventSelected={handleEventSelected}
              onSkip={handleSkipCalendar}
              onCancel={() => setShowCalendarSelector(false)}
            />
          </div>
        </Container>
      </div>
    )
  }

  if (showForm) {
    return (
      <div className="min-h-screen bg-brand-cream py-8">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button
                onClick={handleFormCancel}
                variant="secondary"
                className="text-sm"
              >
                ← Back to Postings
              </Button>
            </div>

            <ScrimmagePostingForm
              team={team}
              scrimmageManager={scrimmageManager}
              existingPosting={editingPosting}
              calendarEvent={selectedCalendarEvent}
              activeCount={activeCount}
              onSuccess={handleFormSuccess}
              onCancel={handleFormCancel}
            />
          </div>
        </Container>
      </div>
    )
  }

  const activePostings = postings.filter(p => p.status === 'active')
  const filledPostings = postings.filter(p => p.status === 'filled')
  const cancelledPostings = postings.filter(p => p.status === 'cancelled')

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="secondary"
                className="text-sm mb-4"
              >
                ← Back to Dashboard
              </Button>
              <div>
                <h1 className="font-heading text-3xl font-bold">
                  Scrimmage Manager
                </h1>
                {limitMessage && (
                  <p className="text-sm text-brand-slate mt-1">
                    {limitMessage}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/scrimmages/bulletin/hockey">
                <Button variant="secondary">
                  Browse Public Board
                </Button>
              </Link>
              <Button 
                variant="primary" 
                onClick={handleNewPosting}
                disabled={!canPost}
              >
                + Post New Scrimmage
              </Button>
            </div>
          </div>

          {/* Upgrade Prompt for Free Users */}
          {!isPro && activeCount > 0 && (
            <Card className="mb-6 bg-brand-cream border-2 border-brand-green">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h3 className="font-medium text-brand-charcoal mb-1">
                    Want unlimited scrimmage postings?
                  </h3>
                  <p className="text-sm text-brand-slate mb-3">
                    Upgrade to Pro for unlimited active postings plus all premium features.
                  </p>
                  <Link to="/pricing">
                    <Button variant="primary" size="sm">
                      Upgrade to Pro
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          )}

          {/* Active Postings */}
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4">
              Active Postings ({activePostings.length})
            </h2>
            {loading ? (
              <Card>
                <div className="text-center py-8 text-brand-slate">Loading...</div>
              </Card>
            ) : activePostings.length === 0 ? (
              <Card>
                <div className="text-center py-8">
                  <p className="text-brand-slate mb-4">No active postings yet.</p>
                  <Button variant="primary" onClick={handleNewPosting}>
                    Post Your First Scrimmage
                  </Button>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {activePostings.map(posting => (
                  <PostingCard
                    key={posting.id}
                    posting={posting}
                    onEdit={() => handleEditPosting(posting)}
                    onMarkFilled={() => handleMarkFilled(posting)}
                    onCancel={() => handleCancelPosting(posting)}
                    onDelete={() => handleDeletePosting(posting)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Filled Postings */}
          {filledPostings.length > 0 && (
            <div className="mb-8">
              <h2 className="font-heading text-xl font-bold mb-4">
                Filled Postings ({filledPostings.length})
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {filledPostings.map(posting => (
                  <PostingCard
                    key={posting.id}
                    posting={posting}
                    onReactivate={() => handleReactivate(posting)}
                    onDelete={() => handleDeletePosting(posting)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Cancelled Postings */}
          {cancelledPostings.length > 0 && (
            <div>
              <h2 className="font-heading text-xl font-bold mb-4">
                Cancelled Postings ({cancelledPostings.length})
              </h2>
              <div className="grid grid-cols-1 gap-4">
                {cancelledPostings.map(posting => (
                  <PostingCard
                    key={posting.id}
                    posting={posting}
                    onReactivate={() => handleReactivate(posting)}
                    onDelete={() => handleDeletePosting(posting)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}

function PostingCard({ posting, onEdit, onMarkFilled, onCancel, onReactivate, onDelete }) {
  const formatDate = (date) => {
    // Parse date as local date (YYYY-MM-DD format)
    // Don't use new Date(date) as it treats it as UTC
    const [year, month, day] = date.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    return localDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (time) => {
    if (!time) return ''
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatTeamLevel = (teamLevel, levelDetail) => {
    const ageGroup = HOCKEY_AGE_GROUPS.find(ag => ag.value === teamLevel)
    const label = ageGroup ? ageGroup.label : teamLevel
    return levelDetail ? `${label} ${levelDetail}` : label
  }

  const isActive = posting.status === 'active'
  const isFilled = posting.status === 'filled'

  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Badge variant={posting.posting_type === 'available' ? 'success' : 'info'}>
              {posting.posting_type === 'available' ? 'Home Ice Available' : 'Seeking Away Game'}
            </Badge>
            <Badge variant="default">
              {formatTeamLevel(posting.team_level, posting.level_detail)}
            </Badge>
            {posting.status !== 'active' && (
              <Badge variant={isFilled ? 'success' : 'default'}>
                {posting.status}
              </Badge>
            )}
            {posting.prefer_non_district && (
              <Badge variant="info">Non-District Preferred</Badge>
            )}
            {posting.home_and_home && (
              <Badge variant="info">Home & Home</Badge>
            )}
          </div>

          <h3 className="font-heading text-lg font-bold text-brand-charcoal mb-1">
            {formatDate(posting.date)} • {formatTime(posting.start_time)}
            {posting.end_time && ` - ${formatTime(posting.end_time)}`}
          </h3>

          {posting.posting_type === 'available' && (
            <p className="text-brand-slate text-sm mb-2">
              {posting.rink_name && `${posting.rink_name}, `}
              {posting.city}, {posting.state}
              {posting.rink_address && ` • ${posting.rink_address}`}
            </p>
          )}

          {posting.referee_type && posting.referee_type !== 'none' && (
            <p className="text-brand-slate text-sm">
              {posting.referee_type === 'coach_led' ? 'Coach-led scrimmage' : 'Paid refs'}
              {posting.scrimmage_fee && ` - $${posting.scrimmage_fee} split`}
            </p>
          )}

          {posting.notes && (
            <p className="text-brand-slate text-sm mt-2 italic">
              {posting.notes}
            </p>
          )}
        </div>

        <div className="flex gap-2 flex-wrap sm:flex-nowrap sm:ml-4 flex-shrink-0">
          {isActive && (
            <>
              {onEdit && (
                <Button variant="secondary" size="sm" onClick={onEdit}>
                  Edit
                </Button>
              )}
              {onMarkFilled && (
                <Button variant="secondary" size="sm" onClick={onMarkFilled}>
                  Mark Filled
                </Button>
              )}
              {onCancel && (
                <Button variant="secondary" size="sm" onClick={onCancel}>
                  Cancel
                </Button>
              )}
            </>
          )}
          {!isActive && onReactivate && (
            <Button variant="secondary" size="sm" onClick={onReactivate}>
              Reactivate
            </Button>
          )}
          {onDelete && (
            <Button 
              variant="secondary" 
              size="sm" 
              onClick={onDelete}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

